// Sketches as files: export one as .efmodel.json, import files or a zip, paste from the clipboard.
// Files go to the browser's downloads; nothing is written to the open folder.
import { parseDocument, SCHEMA_URL, serializeDocument, type EfModelDocument } from '@modelviewer/core';
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import type { SketchRecord } from '../lib/db';
import { flushSketches, importSketch } from '../state/sketches';
import { toast } from '../state/toast';
import { useWorkspace } from '../state/workspace';

export function slug(title: string): string {
  return (
    title
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'sketch'
  );
}

export const sketchFileName = (title: string) => `${slug(title)}.efmodel.json`;

export function sketchText(doc: EfModelDocument): string {
  const { $schema: _schema, ...rest } = doc;
  return serializeDocument({ $schema: SCHEMA_URL, ...rest });
}

function download(name: string, data: BlobPart, type: string): void {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function exportSketch(sketch: SketchRecord): void {
  download(sketchFileName(sketch.doc.title), sketchText(sketch.doc), 'application/json');
}

/** All sketches of the open folder, laid out like `.modelviewer/` so the zip can be unpacked into a repository. */
export async function exportAllSketches(): Promise<void> {
  await flushSketches();
  const { sketches, source } = useWorkspace.getState();
  const list = Object.values(sketches).sort((a, b) => a.created - b.created);
  if (!list.length) {
    toast('There are no sketches to export.');
    return;
  }
  const files: Record<string, Uint8Array> = {};
  const used = new Set<string>();
  for (const s of list) {
    let name = sketchFileName(s.doc.title);
    for (let i = 2; used.has(name); i++) name = sketchFileName(`${s.doc.title}-${i}`);
    used.add(name);
    files[`.modelviewer/sketches/${name}`] = strToU8(sketchText(s.doc));
  }
  download(`${slug(source?.label ?? 'modelviewer')}-sketches.zip`, zipSync(files).slice().buffer, 'application/zip');
}

/** Parses efmodel text; returns the document or the first problem. */
export function readSketchText(text: string): { doc: EfModelDocument } | { error: string } {
  const result = parseDocument(text);
  if (result.document && result.ok) return { doc: result.document };
  const first = result.issues.find((i) => i.severity === 'error');
  return { error: first ? `${first.path ? `${first.path}: ` : ''}${first.message}` : 'Not an efmodel document.' };
}

/** Imports .efmodel.json files and zips of them. Returns the sketches that were added. */
export async function importFiles(files: Iterable<File>): Promise<SketchRecord[]> {
  const added: SketchRecord[] = [];
  const failed: string[] = [];
  const add = async (name: string, text: string) => {
    const read = readSketchText(text);
    if ('error' in read) {
      failed.push(`${name}: ${read.error}`);
      return;
    }
    const record = await importSketch(read.doc);
    if (record) added.push(record);
  };
  for (const file of files) {
    if (file.name.toLowerCase().endsWith('.zip')) {
      const entries = unzipSync(new Uint8Array(await file.arrayBuffer()), { filter: (f) => f.name.endsWith('.efmodel.json') });
      for (const [name, data] of Object.entries(entries)) await add(name, strFromU8(data));
    } else {
      await add(file.name, await file.text());
    }
  }
  if (failed.length) toast(`Could not import ${failed.join('; ')}`, 'error');
  if (added.length) toast(`Imported ${added.length} sketch${added.length > 1 ? 'es' : ''}.`);
  return added;
}
